import { getAppConfig } from '@/utils/env';
import { defineStore, storeToRefs } from '@repo/stores';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { until } from '@vueuse/core';
import { type FabricShareInfo, type Relation, useStyle3d, mergeShareFabricInfo } from '@repo/style3d';
import axios, { type AxiosResponse } from 'axios';
import { joinUrl, setWindowTitle } from '@repo/utils';
import { useLoading } from '@repo/components';
import { useStyle3dStore, useRelationStore } from '@repo/stores';
import { getEnterpriseById } from '@/api/enterprise';

const { OSS_BASE_URL, INDIVIDUAL_SHARES, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const projectRootPath = `${OSS_BASE_URL}${INDIVIDUAL_SHARES}`;
const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

export const useParamStore = defineStore('param', () => {
  const loadingSpin = useLoading();

  const rootPath = computed(() => {
    return joinUrl(projectRootPath, scoId.value);
  });

  const scoId = ref<string>('');
  const fabricId = ref<string | null>(null);

  const { setModel, loadSco, loadFabric, resetCameraViewState } = useStyle3d();
  const style3dStore = useStyle3dStore();
  const { loaded: sdkLoaded, ready: sdkReady } = storeToRefs(style3dStore);

  const relationStore = useRelationStore();

  async function loadScoResource() {
    const { metadata } = await loadRelation();
    const model = metadata?.modelType || 'sco';
    // 初始化标题（优先使用元数据中的企业名称）
    let title = metadata?.enterpriseName ?? '';
    // 如果有企业ID且尚未获取到标题，再查询企业详情
    if (metadata?.enterpriseID && !title) {
      const enterprise = await getEnterpriseById(metadata.enterpriseID);
      // 使用可选链+空值合并简化赋值逻辑
      title = enterprise?.webConfig?.dispalyname ?? title;
    }
    if (title) {
      setWindowTitle(title);
    }

    setModel(model.toUpperCase() as 'GLB' | 'SCO');
    console.time('loadScoResource');
    await until(sdkLoaded).toBe(true);

    let url;
    switch (model) {
      case 'glb':
        url = joinUrl(rootPath.value, '/glb/emu.glb');
        break;
      default:
        url = joinUrl(rootPath.value, '/sco/sco.json');
        break;
    }
    await loadSco(url);
    console.timeEnd('loadScoResource');
  }

  async function loadRelation() {
    const jsonResult: AxiosResponse<Relation> = await axios.get(joinUrl(rootPath.value, 'relation.json'));
    return jsonResult.data;
  }

  async function loadFabricResource() {
    console.time('loadFabricResource');
    await until(sdkReady).toBe(true);

    const json = await loadRelation();
    let shapeList = json.shape;
    let fabricRoot = joinUrl(projectRootPath, scoId.value);

    if (fabricId.value) {
      fabricRoot = joinUrl(fabricRootPath, fabricId.value);
      const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
      const info = infoResult.data;
      shapeList = await mergeShareFabricInfo(json.shape, rootPath.value, info);
    }

    await loadFabric(shapeList, { fabricRootPath: fabricRoot });
    await resetCameraViewState();

    relationStore.setRelation({ ...json, shape: shapeList });
    console.timeEnd('loadFabricResource');
  }

  async function reload() {
    loadingSpin.setLoading(true);
    await loadScoResource();
    await loadFabricResource();
    loadingSpin.setLoading(false);
  }

  async function refreshLoadResource() {
    if (route.name === 'fabric') {
      scoId.value = 'fabric_hanger';
      fabricId.value = route.params.id as string;
    } else {
      scoId.value = route.params.id as string;
      fabricId.value = null;
    }

    await reload();
  }

  onMounted(async () => {
    await refreshLoadResource();
  });

  const route = useRoute();
  watch([() => route.name, () => route.params.id], async () => {
    await refreshLoadResource();
  });

  return { scoId, rootPath };
});
